import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ClipboardList, Search } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/page-header';
import { PriorityBadge, TaskStatusBadge, statusLabelKey } from './task-status';
import { NewTaskDialog } from './new-task-dialog';
import { request } from '@/lib/api';
import { useCanEdit } from '@/lib/auth';
import { taskListSchema, taskStatusSchema, type TaskStatus } from '@/lib/schemas';
import { cn, formatDate, formatDateTime, formatRelative } from '@/lib/utils';

const FILTERS: Array<TaskStatus | 'ALL'> = ['ALL', ...taskStatusSchema.options];

export function TasksPage() {
  const { t } = useTranslation(['tasks', 'common']);
  const canEdit = useCanEdit();
  const [status, setStatus] = useState<TaskStatus | 'ALL'>('ALL');
  const [query, setQuery] = useState('');
  // useDeferredValue — הסינון לא חוסם את ההקלדה. עדיף מ-debounce
  // ידני: React מפסיק רינדור מיושן במקום להמתין בזמן קבוע.
  const deferredQuery = useDeferredValue(query);

  const tasks = useQuery({
    queryKey: ['tasks', { status }],
    queryFn: ({ signal }) =>
      request(`/tasks?take=50${status === 'ALL' ? '' : `&status=${status}`}`, {
        schema: taskListSchema,
        signal,
      }),
  });

  const filtered = tasks.data?.items.filter((task) =>
    deferredQuery.trim() === ''
      ? true
      : `${task.title} ${task.customer?.name ?? ''}`.toLowerCase().includes(deferredQuery.toLowerCase()),
  );

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={canEdit ? <NewTaskDialog /> : undefined}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          {/* start-3 — ב-RTL האייקון בימין, בלי CSS נפרד לכל כיוון. */}
          <Search
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('page.searchPlaceholder')}
            aria-label={t('page.searchLabel')}
            className="ps-9"
          />
        </div>

        <div role="tablist" aria-label={t('page.filterLabel')} className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={status === f}
              onClick={() => setStatus(f)}
              className={cn(
                'h-9 rounded-(--radius-md) px-3 text-xs font-medium transition-colors duration-(--duration-fast)',
                status === f
                  ? 'bg-accent-subtle text-accent'
                  : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
              )}
            >
              {f === 'ALL' ? t('page.all') : t(statusLabelKey(f))}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        {tasks.isLoading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-14 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : tasks.isError ? (
          <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />
        ) : !filtered || filtered.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={query ? t('page.noResults') : t('page.empty')}
            description={
              query
                ? t('page.noResultsHint')
                : t('page.emptyHint')
            }
          />
        ) : (
          // הטבלה גוללת בתוך המכל שלה. גלילה אופקית של ה-body
          // כולו היא באג, לא פיצ'ר.
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] text-sm">
              <caption className="sr-only">{t('page.caption')}</caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken/60 text-start">
                  <Th>{t('page.colTask')}</Th>
                  <Th>{t('page.colCustomer')}</Th>
                  <Th>{t('page.colPriority')}</Th>
                  <Th>{t('page.colStatus')}</Th>
                  <Th>{t('page.colCreated')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((task) => (
                  <tr key={task.id} className="transition-colors hover:bg-surface-hover">
                    <td className="px-4 py-3">
                      <Link
                        to="/tasks/$taskId"
                        params={{ taskId: task.id }}
                        className="font-medium text-fg hover:text-accent"
                      >
                        {task.title}
                      </Link>
                      {task.kind === 'ADMIN' ? (
                        <span className="ms-2 inline-flex items-center gap-1 align-middle">
                          <Badge tone="info">{t('page.forManager')}</Badge>
                          {task.dueAt ? (
                            <span className="text-2xs text-fg-subtle">{t('page.dueBy', { date: formatDate(task.dueAt) })}</span>
                          ) : null}
                        </span>
                      ) : task.scheduledStart ? (
                        <span className="ms-2 align-middle text-2xs text-fg-subtle">{formatDateTime(task.scheduledStart)}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-fg-muted">{task.customer?.name ?? '—'}</td>
                    <td className="px-4 py-3">
                      <PriorityBadge priority={task.priority} />
                    </td>
                    <td className="px-4 py-3">
                      <TaskStatusBadge status={task.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-fg-subtle">
                      <time dateTime={task.createdAt}>{formatRelative(task.createdAt)}</time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-2.5 text-start text-2xs font-medium uppercase tracking-wide text-fg-subtle">
      {children}
    </th>
  );
}
